import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';
import { api } from '../../lib/api';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { AlertIcon } from '../../components/icons';
import { errorMessage, formatDateTime, useLoad } from '../members/hooks';
import { MIN_REASON } from './bulkActions';
import { QUARANTINE_UNIT_LABEL, type OpsConfig, type QuarantineUnit } from './types';

const DEFAULTS: OpsConfig = { quarantine: { value: 12, unit: 'months' }, artFreeRegens: 2 };

/** Campo de motivo del cambio, con el mismo contador del patrón destructivo (design-spec §2). */
function ReasonField({ id, value, onChange, hint }: { id: string; value: string; onChange: (v: string) => void; hint: string }) {
  const [blurred, setBlurred] = useState(false);
  const short = value.trim().length < MIN_REASON;
  const showError = blurred && short;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs font-medium text-ink-muted">
        Motivo del cambio <span className="font-semibold text-error-text">obligatorio</span>
      </label>
      <input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setBlurred(true)}
        aria-invalid={showError || undefined}
        aria-describedby={`${id}-help${showError ? ` ${id}-error` : ''}`}
        className={`w-full rounded-sm border bg-white px-3 py-2 text-sm text-ink ${showError ? 'border-error' : 'border-line'}`}
      />
      <span id={`${id}-help`} className="flex flex-wrap justify-between gap-2 text-xs text-ink-muted">
        <span>{hint}</span>
        <span className="tabular-nums">
          {value.trim().length}/{MIN_REASON} mínimo
        </span>
      </span>
      {showError && (
        <p id={`${id}-error`} role="alert" className="flex items-center gap-1.5 text-xs font-medium text-error-text">
          <AlertIcon className="h-3.5 w-3.5" />
          Escribe al menos {MIN_REASON} caracteres para guardar.
        </p>
      )}
    </div>
  );
}

/** Configuración de la operación (R4.1b, R2.1): cuarentena y regeneraciones gratis, cada cambio con motivo. */
export function OpsConfigPage() {
  const load = useCallback(() => api.get<OpsConfig>('/v1/operator/config'), []);
  const config = useLoad(load);
  const ids = useId();

  const [value, setValue] = useState(String(DEFAULTS.quarantine.value));
  const [unit, setUnit] = useState<QuarantineUnit>(DEFAULTS.quarantine.unit);
  const [quarantineReason, setQuarantineReason] = useState('');
  const [regens, setRegens] = useState(String(DEFAULTS.artFreeRegens));
  const [regensReason, setRegensReason] = useState('');
  const [saving, setSaving] = useState<'quarantine' | 'regens' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!config.data) return;
    setValue(String(config.data.quarantine?.value ?? DEFAULTS.quarantine.value));
    setUnit(config.data.quarantine?.unit ?? DEFAULTS.quarantine.unit);
    setRegens(String(config.data.artFreeRegens ?? DEFAULTS.artFreeRegens));
  }, [config.data]);

  const current = config.data ?? DEFAULTS;

  async function save(which: 'quarantine' | 'regens', body: Partial<OpsConfig>, reason: string, message: string) {
    setSaving(which);
    setNotice(null);
    setError(null);
    try {
      const next = await api.request<OpsConfig>('/v1/operator/config', {
        method: 'PUT',
        body: { quarantine: current.quarantine, artFreeRegens: current.artFreeRegens, ...body, reason: reason.trim() },
      });
      config.setData(next ?? null);
      setNotice(message);
      if (which === 'quarantine') setQuarantineReason('');
      else setRegensReason('');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(null);
    }
  }

  function saveQuarantine(e: FormEvent) {
    e.preventDefault();
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 1) {
      setError('La duración de la cuarentena tiene que ser un número mayor que cero.');
      return;
    }
    void save(
      'quarantine',
      { quarantine: { value: parsed, unit } },
      quarantineReason,
      `La cuarentena de las bajas nuevas queda en ${parsed} ${QUARANTINE_UNIT_LABEL[unit]}. Las bajas ya hechas mantienen su fecha de término.`,
    );
  }

  function saveRegens(e: FormEvent) {
    e.preventDefault();
    const parsed = Number(regens);
    if (!Number.isInteger(parsed) || parsed < 0) {
      setError('Las regeneraciones gratis tienen que ser un número entero de cero o más.');
      return;
    }
    void save('regens', { artFreeRegens: parsed }, regensReason, `Las regeneraciones gratis quedan en ${parsed} por código.`);
  }

  const quarantineOk = quarantineReason.trim().length >= MIN_REASON;
  const regensOk = regensReason.trim().length >= MIN_REASON;

  return (
    <section className="space-y-5">
      <div>
        <h1 className="font-display text-xl font-bold text-ink">Configuración de la operación</h1>
        <p className="text-sm text-ink-muted">
          Valores por defecto de las acciones sobre códigos. Cambiarlos no altera las bajas ni las cuotas ya registradas, y cada cambio queda auditado.
        </p>
      </div>

      <div aria-live="polite">{notice && <Banner tone="success">{notice}</Banner>}</div>
      {error && <Banner tone="error">{error}</Banner>}
      {config.error && <Banner tone="error">{config.error}</Banner>}

      <div className="grid gap-4 lg:grid-cols-2">
        <form onSubmit={saveQuarantine} className="flex flex-col gap-3 rounded-lg border border-line bg-white p-4" aria-label="Cuarentena de códigos">
          <h2 className="font-display text-base font-bold text-ink">Cuarentena de códigos</h2>
          <p className="text-sm text-ink-muted">Tiempo en que un código dado de baja, eliminado o quitado no se puede reasignar a nadie.</p>
          <div className="flex flex-wrap gap-3">
            <label htmlFor={`${ids}-q-value`} className="flex flex-1 flex-col gap-1 text-xs font-medium text-ink-muted">
              Duración
              <input
                id={`${ids}-q-value`}
                type="number"
                min={1}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="rounded-sm border border-line bg-white px-3 py-2 text-sm tabular-nums text-ink"
              />
            </label>
            <label htmlFor={`${ids}-q-unit`} className="flex flex-1 flex-col gap-1 text-xs font-medium text-ink-muted">
              Unidad
              <select
                id={`${ids}-q-unit`}
                value={unit}
                onChange={(e) => setUnit(e.target.value as QuarantineUnit)}
                className="rounded-sm border border-line bg-white px-3 py-2 text-sm text-ink"
              >
                <option value="months">meses</option>
                <option value="days">días</option>
              </select>
            </label>
          </div>
          <ReasonField
            id={`${ids}-q-reason`}
            value={quarantineReason}
            onChange={setQuarantineReason}
            hint="Queda en la auditoría junto al valor anterior y el nuevo."
          />
          <div className="flex justify-end">
            <Button type="submit" disabled={!quarantineOk} loading={saving === 'quarantine'}>
              Guardar cuarentena
            </Button>
          </div>
        </form>

        <form onSubmit={saveRegens} className="flex flex-col gap-3 rounded-lg border border-line bg-white p-4" aria-label="Regeneraciones gratis de QR Art">
          <h2 className="font-display text-base font-bold text-ink">Regeneraciones gratis de QR Art</h2>
          <p className="text-sm text-ink-muted">Versiones nuevas por gusto que no descuentan la cuota del plan, por código.</p>
          <label htmlFor={`${ids}-regens`} className="flex flex-col gap-1 text-xs font-medium text-ink-muted">
            Por código
            <input
              id={`${ids}-regens`}
              type="number"
              min={0}
              max={10}
              value={regens}
              onChange={(e) => setRegens(e.target.value)}
              className="rounded-sm border border-line bg-white px-3 py-2 text-sm tabular-nums text-ink"
            />
          </label>
          <ReasonField id={`${ids}-r-reason`} value={regensReason} onChange={setRegensReason} hint="Queda en la auditoría junto al valor anterior y el nuevo." />
          <div className="flex justify-end">
            <Button type="submit" disabled={!regensOk} loading={saving === 'regens'}>
              Guardar regeneraciones
            </Button>
          </div>
        </form>
      </div>

      <p className="text-xs text-ink-muted">
        {config.data?.updatedAt
          ? `Último cambio: ${formatDateTime(config.data.updatedAt)}${config.data.updatedBy ? ` · ${config.data.updatedBy}` : ''}.`
          : 'Los cambios quedan en la auditoría con el valor anterior, el nuevo y el motivo.'}
      </p>
    </section>
  );
}
